export const en = {
  seo: {
    siteTitle: 'Dracula Tools',
    siteKeywords:
      'web tools, developer tools, react, vite, json formatter, password generator, color converter, pomodoro, dracula theme',
  },
  common: {
    searchLabel: 'Tool search',
    searchPlaceholder: 'Search tools...',
    language: 'Language',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    loading: 'Loading tool...',
    copied: 'Copied!',
    clear: 'Clear',
    copy: 'Copy',
    generate: 'Generate',
    encode: 'Encode',
    decode: 'Decode',
    format: 'Format',
    minify: 'Minify',
    validate: 'Validate',
    result: 'Result',
    noSearchResults: 'No tools matched your search.',
    focus: 'Focus',
    shortBreak: 'Short break',
    longBreak: 'Long break',
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    reset: 'Reset',
    skip: 'Skip',
    elapsed: 'Elapsed',
    today: 'Today',
    week: 'This week',
    total: 'Total',
    streak: 'Current streak',
  },
  navigation: {
    categories: {
      core: 'Core tools',
      formatters: 'Formatters & validators',
      misc: 'More',
    },
    tools: {
      passwordGenerator: {
        name: 'Password generator',
        description:
          'Generate strong passwords with adjustable length, count, and symbol rules.',
      },
      characterCounter: {
        name: 'Character counter',
        description:
          'Count characters, words, lines, and repeated words in any text.',
      },
      htmlSpaces: {
        name: 'HTML spaces',
        description:
          'Insert non-breaking and special spacing entities for HTML and typography.',
      },
      encodingConverter: {
        name: 'Encoding converter',
        description:
          'Decode mojibake and convert legacy encodings back to readable UTF-8 text.',
      },
      base64: {
        name: 'Base64 encoder',
        description: 'Encode and decode UTF-8 text in Base64.',
      },
      jsonFormatter: {
        name: 'JSON formatter',
        description:
          'Format, validate, and minify JSON with readable syntax highlighting.',
      },
      colorConverter: {
        name: 'Color converter',
        description:
          'Convert colors across HEX, RGB, HSL, and ARGB while exploring palettes.',
      },
      urlTool: {
        name: 'URL encoder',
        description:
          'Encode and decode URL-safe values for query strings and paths.',
      },
      htmlEntities: {
        name: 'HTML entities',
        description: 'Encode and decode HTML entities safely.',
      },
      iphoneCountdown: {
        name: 'iPhone countdown',
        description:
          'Track the next expected iPhone launch event and review recent release history.',
      },
      pomodoro: {
        name: 'Pomodoro timer',
        description:
          'Run focused work sessions with saved settings, stats, sounds, and notifications.',
      },
    },
  },
  tools: {
    passwordGenerator: {
      outputLabel: 'Generated password',
      uppercase: 'Uppercase letters (A-Z)',
      lowercase: 'Lowercase letters (a-z)',
      numbers: 'Numbers (0-9)',
      symbols: 'Special characters (!@#$%)',
      avoidAmbiguous: 'Avoid ambiguous characters (1, l, I, 0, O)',
      passwordLength: 'Password length',
      passwordCount: 'Password count',
      copyAll: 'Copy all',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      tipsTitle: 'Password security tips',
      tips: [
        'Use a password with at least 12 characters.',
        'Mix uppercase, lowercase, numbers, and symbols.',
        'Avoid reusing the same password across services.',
        'Rotate sensitive credentials on a schedule.',
        'Store passwords in a trusted password manager.',
      ],
    },
    characterCounter: {
      placeholder: 'Type or paste text here...',
      characters: 'Characters',
      words: 'Words',
      lines: 'Lines',
      withoutSpaces: 'Without spaces',
      frequencyTitle: 'Frequency analysis',
      frequencyLead: 'Top 5 most frequent words:',
      notEnoughData: 'Not enough data for analysis.',
    },
    htmlSpaces: {
      selectLabel: 'Choose an entity',
      previewTitle: 'Preview',
      previewPrefix: 'Text',
      previewSuffix: 'with spacing',
      tableTitle: 'HTML spacing reference',
      columns: {
        name: 'Name',
        entity: 'Entity',
        description: 'Description',
        width: 'Width',
      },
      options: {
        nbsp: 'Non-breaking space',
        ensp: 'En space',
        emsp: 'Em space',
        thinsp: 'Thin space',
        zwsp: 'Zero-width space',
        ndash: 'En dash',
        mdash: 'Em dash',
      },
      rows: [
        {
          id: 'nbsp',
          entity: '&nbsp;',
          description: 'Prevents a line break between words.',
          width: 'Regular space width',
        },
        {
          id: 'ensp',
          entity: '&ensp;',
          description: 'Spacing equal to the width of the letter N.',
          width: '0.5em',
        },
        {
          id: 'emsp',
          entity: '&emsp;',
          description: 'Spacing equal to the width of the letter M.',
          width: '1em',
        },
        {
          id: 'thinsp',
          entity: '&thinsp;',
          description: 'A narrow spacing entity for fine typography.',
          width: '0.2em',
        },
        {
          id: 'zwsp',
          entity: '&zwsp;',
          description: 'Invisible break opportunity without visible spacing.',
          width: '0',
        },
      ],
    },
    encodingConverter: {
      encodingLabel: 'Encoding',
      inputLabel: 'Input text',
      inputPlaceholder: 'Paste text with a broken encoding here...',
      outputLabel: 'Converted result',
      outputPlaceholder: 'Converted text will appear here...',
      copyResult: 'Copy result',
      errorPrefix: 'Encoding error',
    },
    base64: {
      tabs: {
        encode: 'Encode',
        decode: 'Decode',
      },
      encodeInputLabel: 'Source text',
      encodeInputPlaceholder: 'Enter text to encode as Base64...',
      encodeOutputLabel: 'Encoded result',
      encodeOutputPlaceholder: 'The Base64 result will appear here...',
      decodeInputLabel: 'Base64 string',
      decodeInputPlaceholder: 'Enter Base64 to decode...',
      decodeOutputLabel: 'Decoded result',
      decodeOutputPlaceholder: 'The decoded text will appear here...',
      encodeError: 'Encoding error: {{message}}',
      decodeError: 'Decoding error: {{message}}',
    },
    jsonFormatter: {
      inputLabel: 'Enter JSON',
      inputPlaceholder: 'Paste your JSON here...',
      outputLabel: 'Formatted output',
      copyResult: 'Copy result',
      valid: 'JSON is valid.',
      formatError: 'Formatting error: {{message}}',
      minifyError: 'Minify error: {{message}}',
      validateError: 'Validation error: {{message}}',
    },
    colorConverter: {
      pickerTitle: 'Color picker',
      paletteTitle: 'Color palette',
      paletteLead: 'Related colors based on the selected color.',
      hex: 'HEX',
      rgb: 'RGB',
      hsl: 'HSL',
      rgbDecimal: 'RGB Decimal (0-1)',
      argb: 'ARGB (0-255)',
      argbDecimal: 'ARGB Decimal (0-1)',
      copyHex: 'Copy HEX',
      copyRgb: 'Copy RGB',
      copyHsl: 'Copy HSL',
      copyArgb: 'Copy ARGB',
      baseColor: 'Base: {{color}}',
      complementary: 'Complementary: {{color}}',
      copiedSwatch: 'Copied!',
      hints: {
        hex: 'HEX format: #RRGGBB or RRGGBB (for example #6272A4).',
        rgb: 'RGB format: rgb(R, G, B) with values from 0 to 255.',
        hsl: 'HSL format: hsl(H, S%, L%) where hue is 0-360.',
        rgbDecimal: 'RGB decimal format: rgb(R, G, B) with values from 0 to 1.',
        argb: 'ARGB format: argb(A, R, G, B) with values from 0 to 255.',
        argbDecimal:
          'ARGB decimal format: argb(A, R, G, B) with values from 0 to 1.',
      },
    },
    urlTool: {
      tabs: {
        encode: 'Encode',
        decode: 'Decode',
      },
      encodeInputLabel: 'Source text',
      encodeInputPlaceholder: 'Enter text for URL encoding...',
      encodeOutputLabel: 'Encoded result',
      encodeOutputPlaceholder: 'The encoded URL will appear here...',
      decodeInputLabel: 'Encoded URL',
      decodeInputPlaceholder: 'Enter encoded URL text to decode...',
      decodeOutputLabel: 'Decoded result',
      decodeOutputPlaceholder: 'The decoded URL will appear here...',
      decodeError: 'Decoding error: invalid URL escape sequence.',
    },
    htmlEntities: {
      tabs: {
        encode: 'Encode',
        decode: 'Decode',
      },
      encodeInputLabel: 'Source text',
      encodeInputPlaceholder: 'Enter text to encode as HTML entities...',
      encodeOutputLabel: 'Encoded result',
      encodeOutputPlaceholder: 'The encoded HTML entities will appear here...',
      decodeInputLabel: 'HTML entities',
      decodeInputPlaceholder: 'Enter HTML entities to decode...',
      decodeOutputLabel: 'Decoded result',
      decodeOutputPlaceholder: 'The decoded text will appear here...',
    },
    iphoneCountdown: {
      eventLead: 'The next Apple presentation is expected on',
      eventTail: 'at Apple Park in California.',
      historyTitle: 'iPhone release history',
      columns: {
        model: 'Model',
        date: 'Release date',
        features: 'Key features',
      },
      units: {
        days: 'days',
        hours: 'hours',
        minutes: 'minutes',
        seconds: 'seconds',
      },
      history: [
        {
          model: 'iPhone 18 Pro / Pro Max',
          date: '18 September 2026',
          features:
            'A20 Pro chip (2nm) with 12 GB RAM, 48 MP main camera with variable aperture, improved cooling. Up to 36 hours of video playback (45 on the Pro Max). Siri with Visual Intelligence in the Camera app, LiDAR, USB-C, up to 2 TB storage.',
        },
        {
          model: 'iPhone Duo',
          date: '23 October 2026',
          features:
            'The first foldable iPhone: 7.6" inner and 5.4" outer displays, A20 Pro chip, Touch ID in the side button. On sale 23 October 2026, from $1999.',
        },
        {
          model: 'iPhone 17',
          date: '19 September 2025',
          features:
            'A19 chip, 6.3" 120 Hz display, dual 48 MP cameras, new Apple N1 networking chip, USB-C with MagSafe and Qi2, five colors.',
        },
        {
          model: 'iPhone 17 Pro / Pro Max',
          date: '19 September 2025',
          features:
            'A19 Pro chip with 12 GB RAM, triple 48 MP cameras with LiDAR and the longest telephoto in the lineup, vapor chamber cooling, up to 2 TB storage.',
        },
        {
          model: 'iPhone Air',
          date: '19 September 2025',
          features:
            'The thinnest iPhone ever at 5.6 mm with a titanium frame. 6.5" 120 Hz display, A19 Pro chip, 48 MP and 18 MP cameras, eSIM only.',
        },
        {
          model: 'iPhone 16e',
          date: '28 February 2025',
          features:
            'The affordable iPhone: A18 chip, 48 MP camera with a 2x sensor crop, USB-C, Apple Intelligence support, 6.1" display, from $599.',
        },
        {
          model: 'iPhone 16',
          date: '20 September 2024',
          features:
            'A18 chip and Apple Intelligence, Camera Control button, Action button, 48 MP Fusion camera with 2x telephoto and macro, a big battery life jump, five bright colors.',
        },
        {
          model: 'iPhone 15',
          date: '22 September 2023',
          features:
            'Dynamic Island on the base models, 48 MP camera with 2x telephoto, USB-C, A16 chip, Roadside Assistance via satellite.',
        },
        {
          model: 'iPhone 14',
          date: '16 September 2022',
          features:
            'A15 chip, Crash Detection and Emergency SOS via satellite, improved low-light camera, Plus model with a 6.7" display.',
        },
        {
          model: 'iPhone 13',
          date: '24 September 2021',
          features:
            'A15 chip, dual camera with Cinematic mode, Ceramic Shield, up to 2.5 hours more battery life, brighter 800-nit display.',
        },
      ],
    },
    pomodoro: {
      workDuration: 'Work session (min)',
      shortBreakDuration: 'Short break (min)',
      longBreakDuration: 'Long break (min)',
      sessionsCount: 'Sessions before a long break',
      autoStartBreaks: 'Automatically start breaks',
      autoStartPomodoros: 'Automatically start work sessions',
      enableNotifications: 'Enable notifications',
      enableSounds: 'Enable sounds',
      settingsTitle: 'Settings',
      statsTitle: 'Statistics',
      aboutTitle: 'About the Pomodoro technique',
      resetSettings: 'Reset settings',
      resetStats: 'Reset statistics',
      workCompleteTitle: 'Focus session complete!',
      workCompleteBody: 'Time for a break.',
      shortBreakCompleteTitle: 'Break finished!',
      shortBreakCompleteBody: 'Time to get back to work.',
      longBreakCompleteTitle: 'Long break finished!',
      longBreakCompleteBody: 'Time to get back to work.',
      resetSettingsConfirm: 'Reset Pomodoro settings to their defaults?',
      resetStatsConfirm: 'Reset all Pomodoro statistics?',
      aboutIntro:
        'The Pomodoro technique is a time-management method built around focused work intervals followed by deliberate breaks.',
      aboutSteps: [
        'Pick one task to work on.',
        'Set a Pomodoro timer, usually for 25 minutes.',
        'Work until the timer completes.',
        'Take a short break, usually 5 minutes.',
        'After four sessions, take a longer break.',
      ],
      aboutOutro:
        'Regular breaks help you sustain focus, reduce fatigue, and keep momentum over longer work blocks.',
    },
  },
  cookies: {
    ariaLabel: 'Cookie notice',
    text: 'We use cookies and local storage to remember your preferences and — with your consent — to collect anonymous visit statistics (Yandex.Metrika). Learn more in our',
    accept: 'Accept',
    decline: 'Decline',
  },
  pages: {
    contactNote: 'Have a question, an idea, or a suggestion? Reach us via',
    contactLinkLabel: 'the contact form on devdom.ru',
    about: {
      metaTitle: 'About | Dracula Tools',
      metaDescription:
        'About Dracula Tools — a free collection of developer utilities by the DEVDOM studio.',
      heading: 'About',
      intro:
        'Dracula Tools is a compact collection of free utilities for web developers and anyone working with text, code, and data. Everything lives on one site: password generator, character counter, encoding converters, Base64, JSON formatter, color converter, and more.',
      sections: [
        {
          title: 'The Dracula theme',
          paragraphs: [
            'The site is built around the Dracula dark theme — one of the most popular color schemes for code editors and terminals. That is where the name comes from: Dracula Tools.',
          ],
        },
        {
          title: 'Everything runs in your browser',
          paragraphs: [
            'The tools do not send your data anywhere: passwords, text, JSON, and other values are processed locally and never leave your browser. Generated passwords are not stored — you can always generate new ones.',
          ],
        },
        {
          title: 'Who builds the project',
          paragraphs: [
            'The project is maintained by the DEVDOM studio: we build websites, e-commerce, integrations, and AI automation for small and medium businesses. The toolbox keeps growing — some tools come straight from our own daily work.',
          ],
        },
      ],
    },
    privacy: {
      metaTitle: 'Privacy policy | Dracula Tools',
      metaDescription:
        'How Dracula Tools handles data: every tool runs locally, and analytics loads only after your consent.',
      heading: 'Privacy policy',
      updated: 'Last updated: 12 October 2026',
      intro:
        'This policy explains what data is processed when you use Dracula Tools (tools.devdom.ru). The site operator is the DEVDOM studio (devdom.ru).',
      sections: [
        {
          title: 'Tool data',
          paragraphs: [
            'Every tool runs entirely in your browser. Text, passwords, JSON, and other values you enter are never sent to our servers and are not stored. No registration is required and we do not collect personal data.',
          ],
        },
        {
          title: 'Browser local storage',
          paragraphs: [
            'To remember your settings — the selected language, tool options, the last opened tool, and your cookie choice — the site uses your browser local storage (localStorage). This data lives only on your device and is removed when you clear the site data in your browser.',
          ],
        },
        {
          title: 'Analytics and cookies',
          paragraphs: [
            'With your consent, the site loads Yandex.Metrika, a web analytics service that collects anonymous visit statistics: pages viewed, device type, approximate region. Metrika uses cookies. If you decline, analytics is not loaded; you can change your decision at any time by clearing the site data in your browser.',
          ],
        },
        {
          title: 'Third-party services',
          paragraphs: [
            'The site loads fonts (Google Fonts) and an icon library (cdnjs CDN), and after consent — Yandex.Metrika. As part of normal internet traffic, these services receive technical request data such as the IP address and browser details.',
          ],
        },
        {
          title: 'Your rights',
          paragraphs: [
            'You can withdraw analytics consent and delete local site data at any time by clearing the site data in your browser settings. For any questions about data processing, contact us through the contact form.',
          ],
        },
        {
          title: 'Policy changes',
          paragraphs: [
            'This policy may be updated: the current version is always published on this page.',
          ],
        },
      ],
    },
  },
  footer: {
    about: 'About',
    privacy: 'Privacy policy',
    contact: 'Contact',
    madeBy: 'Made at DEVDOM',
  },
} as const;
